<template>
  <div class="container--fluid d-flex flex-column gap-6 mx-4">
    <v-row class="mt-4">
      <v-col lg="8" md="12" class="d-flex flex-column gap-10">
        <div class="warehouse-settings">
          <h3 class="text-black mb-4">
            {{ $t('warehouse.pages.settings.headers.warehouseSettings') }}
          </h3>
          <settings-expansion-panels
            :read-only="readOnly"
            :entity-parent="novaCore.DatabaseEntities.Org"
            :entity-key="novaCore.DatabaseEntities.Warehouse"
            :should-be-expanded="false"
            :entity-parent-obj="editingWarehouse"
            @update-settings="data => (warehouseSettings = data)"
            :entity-settings="warehouse?.settings || {}" />
        </div>

        <div class="refnumber-settings">
          <h3 class="text-black mb-4">
            {{ $t('warehouse.pages.settings.headers.refNumberValidationSetup') }}
            <a
              href="https://community.loadsmart.com/hc/en-us/articles/14946368437907-PO-Ref-Number-Validation-Implementation"
              target="_blank"
              rel="noopener"
              class="link small ml-2">
              {{ $t('warehouse.pages.settings.links.learnMoreHere') }}
            </a>
          </h3>
          <v-expansion-panels multiple>
            <v-expansion-panel key="refNumberValidationSetup">
              <v-expansion-panel-title ripple>
                {{ $t('global.basicTerms.setup') }}
              </v-expansion-panel-title>
              <v-divider />
              <v-expansion-panel-text>
                <div class="py-4">
                  <v-select
                    data-testid="validation-version-select"
                    :disabled="readOnly"
                    :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                    variant="outlined"
                    persistent-hint
                    persistent-placeholder
                    :label="$t('global.basicTerms.version')"
                    item-title="text"
                    item-value="value"
                    v-model="editingWarehouse.refNumberValidationVersion"
                    :items="refNumberValidationVersionItems" />
                </div>

                <div>
                  <label>
                    {{ settingsMeta.overrideExternalAppointmentValidation.label }}
                    <help-icon-tooltip
                      v-if="settingsMeta.overrideExternalAppointmentValidation.description"
                      icon-style-class="grey">
                      {{ settingsMeta.overrideExternalAppointmentValidation.description }}
                    </help-icon-tooltip>
                  </label>
                  <v-select
                    data-testid="override-appointment-validation-setting"
                    id="override-appointment-validation-setting"
                    :items="[
                      {
                        title: 'Yes, allow appointments to be created even if validation fails',
                        value: true
                      },
                      {
                        title: 'No, prevent appointment creation on validation failure',
                        value: false
                      }
                    ]"
                    v-if="
                      !novaCore.isSettingHidden(
                        'overrideExternalAppointmentValidation',
                        settingsMeta.overrideExternalAppointmentValidation,
                        editingWarehouse.settings
                      )
                    "
                    v-model="editingWarehouse.settings.overrideExternalAppointmentValidation"
                    :disabled="
                      !novaCore.areSettingRequirementsMet(
                        'overrideExternalAppointmentValidation',
                        settingsMeta.overrideExternalAppointmentValidation,
                        editingWarehouse.settings
                      ) || readOnly
                    "
                    :hint="settingsMeta.overrideExternalAppointmentValidation.note"
                    clearable
                    persistent-hint
                    persistent-placeholder
                    variant="outlined">
                    <template #message="{ message }">
                      <span v-html="message"></span>
                    </template>
                  </v-select>
                </div>

                <div
                  v-if="
                    editingWarehouse.refNumberValidationVersion ===
                    novaCore.RefNumberValidationVersion.V2
                  "
                  class="py-4">
                  <div>
                    <label>
                      {{ settingsMeta.technicalContactEmail.label }}
                      <help-icon-tooltip
                        v-if="settingsMeta.technicalContactEmail.description"
                        icon-style-class="grey">
                        {{ settingsMeta.technicalContactEmail.description }}
                      </help-icon-tooltip>
                    </label>
                    <v-text-field
                      data-testid="technical-contact-email-address"
                      id="technical-contact-email-address"
                      :rules="[...$validator.rules.email]"
                      v-if="
                        !novaCore.isSettingHidden(
                          'technicalContactEmailAddress',
                          settingsMeta.technicalContactEmail,
                          editingWarehouse.settings
                        )
                      "
                      v-model="editingWarehouse.settings.technicalContactEmailAddress"
                      :disabled="
                        !novaCore.areSettingRequirementsMet(
                          'technicalContactEmailAddress',
                          settingsMeta.technicalContactEmail,
                          editingWarehouse.settings
                        ) || readOnly
                      "
                      :hint="settingsMeta.technicalContactEmail.note"
                      clearable
                      persistent-hint
                      persistent-placeholder
                      variant="outlined"
                      type="email">
                      <template #message="{ message }">
                        <span v-html="message"></span>
                      </template>
                    </v-text-field>
                  </div>

                  <div class="mt-4">
                    <label>
                      {{ settingsMeta.throttleMinutes.label }}
                      <help-icon-tooltip
                        v-if="settingsMeta.throttleMinutes.description"
                        icon-style-class="grey">
                        {{ settingsMeta.throttleMinutes.description }}
                      </help-icon-tooltip>
                    </label>
                    <v-text-field
                      data-testid="external-validation-server-error-notify-throttle-minutes"
                      id="external-validation-server-error-notify-throttle-minutes"
                      :rules="[
                        value =>
                          value >= settingsMeta.throttleMinutes.minValue ||
                          $t(
                            'warehouse.pages.settings.messages.valueMustBeGreaterThanOrEqualToThrottleMinutes',
                            { throttleMinutes: settingsMeta.throttleMinutes.minValue }
                          ),
                        value =>
                          value <= settingsMeta.throttleMinutes.maxValue ||
                          $t(
                            'warehouse.pages.settings.messages.valueMustBeLessThanOrEqualToThrottleMinutes',
                            { throttleMinutes: settingsMeta.throttleMinutes.maxValue }
                          )
                      ]"
                      v-if="
                        !novaCore.isSettingHidden(
                          'technicalContactEmailAddress',
                          settingsMeta.throttleMinutes,
                          editingWarehouse.settings
                        )
                      "
                      v-model="
                        editingWarehouse.settings.externalValidationServerErrorNotifyThrottleMinutes
                      "
                      :disabled="
                        !novaCore.areSettingRequirementsMet(
                          'technicalContactEmailAddress',
                          settingsMeta.throttleMinutes,
                          editingWarehouse.settings
                        ) || readOnly
                      "
                      :hint="settingsMeta.throttleMinutes.note"
                      persistent-hint
                      persistent-placeholder
                      variant="outlined"
                      type="number">
                      <template #message="{ message }">
                        <span v-html="message"></span>
                      </template>
                    </v-text-field>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-title ripple>
                {{ $t('warehouse.pages.settings.headers.credentials') }}
              </v-expansion-panel-title>
              <v-divider />
              <v-expansion-panel-text>
                <div class="py-4">
                  <v-text-field
                    data-testid="validation-validator-url-input"
                    :disabled="readOnly"
                    :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                    spellcheck="false"
                    type="url"
                    v-model="editingWarehouse.refNumberValidationUrl"
                    :rules="$validator.rules.url"
                    class="mb-4"
                    :label="$t('warehouse.pages.settings.labels.validatorUrl')" />

                  <div
                    v-if="
                      editingWarehouse.refNumberValidationVersion ===
                      novaCore.RefNumberValidationVersion.V2
                    ">
                    <v-text-field
                      data-testid="validation-passcode-input"
                      :disabled="readOnly"
                      :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                      spellcheck="false"
                      type="text"
                      v-model="editingWarehouse.refNumberValidationPasscode"
                      :label="$t('warehouse.pages.settings.labels.passcodeOptionalBearerToken')" />
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="scheduleportal-settings">
          <h3 class="text-black mb-4">
            {{ $t('warehouse.pages.settings.headers.schedulingPortalAvailability') }}
          </h3>
          <v-expansion-panels multiple>
            <v-expansion-panel key="schedulePortalAvailability">
              <v-expansion-panel-title ripple>
                {{ $t('warehouse.pages.settings.headers.carrierVisibility') }}
              </v-expansion-panel-title>
              <v-divider />
              <v-expansion-panel-text>
                <div class="py-4">
                  <carrier-scheduling-toggle
                    class="pl-2 pb-2"
                    entity="Warehouse"
                    :disabled="!$rolePermissions.canUpdateWarehouse || readOnly"
                    v-model="editingWarehouse.allowCarrierScheduling" />

                  <div
                    v-if="
                      editingWarehouse.allowCarrierScheduling &&
                      $carrierViewAllowedUserRoles.includes($me.role) &&
                      !readOnly
                    ">
                    <div class="d-flex">
                      <primary-button
                        small
                        test-id="copy-warehouse-button"
                        before-icon="link"
                        class="mr-4"
                        @click="copyWarehouseLink">
                        {{ $t('warehouse.pages.settings.buttons.copyWarehouseLinkToShare') }}
                      </primary-button>
                      <preview-as-carrier-button
                        :warehouse="warehouse"
                        :disabled="!warehouse.allowCarrierScheduling" />
                    </div>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-title ripple>
                {{ $t('warehouse.pages.settings.headers.experimentalFeature') }}
              </v-expansion-panel-title>
              <v-divider />
              <v-expansion-panel-text>
                <div class="py-4">
                  <p>
                    <caution-message>
                      {{
                        $t(
                          'warehouse.pages.settings.messages.thisChangesCarrierAvailabilityMakeSureYouUnderstandImplications'
                        )
                      }}:
                    </caution-message>
                    <i18n-t
                      keypath="warehouse.pages.settings.messages.preventSurpriseAppointmentsByRemovingIntervals"
                      tag="span">
                      <template #input>
                        <text-field
                          v-model="editingWarehouse.intervalTrimForCarriers"
                          ref="intervalTrimForCarriers"
                          class="inline-text-field my-0 no-spinner text-center"
                          density="compact"
                          color="secondary"
                          hide-details
                          variant="underlined"
                          type="number" />
                      </template>
                    </i18n-t>
                    .
                  </p>
                  <p class="pt-4">
                    {{
                      $t(
                        'warehouse.pages.settings.messages.intervalInThisContextRefersToAContinuousBlockOfWarehouseOpenHours'
                      )
                    }}
                  </p>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <action-group
          class="pr-0 pt-8"
          :confirm-label="$t('warehouse.pages.settings.buttons.saveSettings')"
          :hide-cancel="true"
          cancel-color="error"
          :confirm-button-tooltip="readOnly ? novaCore.getRoleActionError() : ''"
          :disable-confirm="!$rolePermissions.canUpdateWarehouse || readOnly"
          @confirm="submit"></action-group>
      </v-col>
    </v-row>

    <v-dialog
      :model-value="Boolean(notificationConfigKey)"
      :retain-focus="false"
      width="auto"
      @keydown.esc="notificationConfigKey = null">
      <template v-slot:default>
        <notification-config-form
          v-if="notificationConfigKey"
          :warehouse-id="warehouse.id"
          :settings="warehouse.settings || {}"
          :notification-type="notificationConfigKey"></notification-config-form>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import editWarehouseMixin from '@/modules/warehouses/editWarehouseMixin';

export default {
  mixins: [editWarehouseMixin],
  computed: {
    settingsMeta() {
      return {
        throttleMinutes:
          this.entitySettingsMetaData.externalValidationServerErrorNotifyThrottleMinutes,
        technicalContactEmail: this.entitySettingsMetaData.technicalContactEmailAddress,
        overrideExternalAppointmentValidation:
          this.novaCore.Settings.overrideExternalAppointmentValidation
      };
    },
    entitySettingsMetaData() {
      return this.novaCore.getTranslatedSettings(this.novaCore.DatabaseEntities.Warehouse);
    }
  },
  beforeMount() {
    this.warehouseSettings = { ...(this.warehouse?.settings || {}) };
    this.setEditingWarehouse();
  },
  mounted() {
    this.$eventHub.$on('set-edit-notification-key', notificationKey => {
      this.notificationConfigKey = notificationKey;
    });
    this.richTextInputReady = true;
  },
  destroyed() {
    this.$eventHub.$off('set-edit-notification-key');
  }
};
</script>
<style scoped lang="scss">
.link {
  font-weight: normal !important;
  font-size: 14px;
}
.text-black {
  color: #000 !important;
}
</style>
